import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useScript from '../../../../../../../_hooks/useScript';
import { HiddenLeversRiskScoreLoading } from '../HiddenLeversRiskScore.loading';
import { ErrorState } from '../../../../../../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { Box } from '../../../../../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../../../../../CommonComponents/OPL/Text/Text';
import { Button } from '../../../../../../../CommonComponents/OPL/Button/Button';
import { Select3dRTQResult } from '../../../../../../RiskTolerance/RiskToleranceSelector';
import { Card } from '../../../../../../../CommonComponents/OPL/Card/Card';
import { notifyPdfLoadedSection } from '../../../../../../../PDFRenderer/PDFRendererActions';
import { PdfSectionType } from '../../../../../../../PDFRenderer/PDFRendererTypes';
import {
  isPdfArea,
  selectCanDoRiskQuestionnaire,
} from '../../../../../../../CommonSelectors/CommonAreaSelector';

const ORDERED_RESULTS = ['capacity', 'tolerance', 'composure'];

const DIAL_COLOR_MAP = {
  tolerance: {
    color: '#EB6A37',
  },
  capacity: {
    color: '#9061F8',
  },
  composure: {
    color: '#0694A2',
  },
};

interface RiskScoreProps {
  onRetakeAssessment?: () => void;
  result: Select3dRTQResult;
}

export const RiskScore = ({ onRetakeAssessment, result }: RiskScoreProps) => {
  const dispatch = useDispatch();

  const { riskScore, riskScoreDescription, ...rest } = result;

  const isPDF = useSelector(isPdfArea);
  const canDoRiskQuestionnaire = useSelector(selectCanDoRiskQuestionnaire);

  const [isScriptLoaded, scriptError] = useScript(`${ADV.settings.endpoints.assets}js/oat_dial.js`);

  useEffect(() => {
    if (isScriptLoaded) {
      dispatch(notifyPdfLoadedSection(PdfSectionType.RiskProfile3d));
    }
  }, [isScriptLoaded]);

  if (!isScriptLoaded) {
    return <HiddenLeversRiskScoreLoading />;
  }

  if (scriptError) {
    return (
      <ErrorState
        height="100%"
        message="There was an error loading the risk score. Please try again later."
      />
    );
  }

  return (
    <Card>
      <Box
        display="flex"
        flexDirection="column"
        width="100%"
        alignItems="center"
        justifyContent="center"
        py="32"
      >
        <oat-dial score={riskScore} animate={`${!isPDF}`} />

        <Text textAlign="center" maxWidth="540px" m="auto" p="32" fontWeight={500}>
          {Object.values(rest).includes(null)
            ? 'To see your risk capacity, risk composure and risk tolerance scores, please retake your assessment.'
            : riskScoreDescription}
        </Text>
      </Box>

      <Box
        display="flex"
        justifyContent="space-evenly"
        as="ul"
        css="list-style: none"
        px="32"
        pt="0"
        pb="32"
        gridGap="16"
      >
        {ORDERED_RESULTS.filter(key => rest[key] !== null).map(key => (
          <Box display="flex" as="li" width="100%" key={key}>
            <oat-dial
              score={rest[key].score}
              color={DIAL_COLOR_MAP[key].color}
              type="sm-dial"
              animate={`${!isPDF}`}
            />

            <Box pl="16">
              <Text color="gray.800" fontSize="h6" fontWeight={600} textTransform="capitalize">
                {key}
              </Text>

              <Text color="gray.600" fontWeight={500}>
                {rest[key].description}
              </Text>
            </Box>
          </Box>
        ))}
      </Box>

      {canDoRiskQuestionnaire && onRetakeAssessment && (
        <Box pb="32" css="text-align: center">
          <Button variant="link" onClick={onRetakeAssessment}>
            Retake My Assessment
          </Button>
        </Box>
      )}
    </Card>
  );
};
